import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import Button from "../../../components/Button/Button";
import Loader from "../../../components/Loader/Loader";
import { useStudentSession } from "../context/StudentSessionContext";
import { useMeetingWithTypes } from "./hooks/useMeetingWithTypes";
import { createAppointment } from "../../../services/StudentPortal/StudentAppointmentService";
import { handleApiError } from "../../../utils/errorHandler";
import "./Appointments.css";

// No API backs these - the real screenshot's own example durations
// (15/30/45/60 minutes) are used as the fixed preset list.
const DURATION_OPTIONS = [
  { value: 15, label: "15 Minutes" },
  { value: 30, label: "30 Minutes" },
  { value: 45, label: "45 Minutes" },
  { value: 60, label: "60 Minutes" },
];

// Meeting-with-type options aren't confirmed against a real payload yet -
// try a few reasonable field-name variants for the display label.
const pick = (item, keys, fallback = "") => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const EMPTY_FORM = { meetingWithId: "", duration: "", purpose: "" };

/**
 * "Book Appointment" - its own page (not a modal), reached from the
 * Appointments list's "+ Create Appointment" button.
 */
const StudentAppointmentCreate = () => {
  const navigate = useNavigate();
  const { student } = useStudentSession();
  const { types, isLoading: isLoadingTypes, isFeatureUnavailable } = useMeetingWithTypes();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.meetingWithId) nextErrors.meetingWithId = "Please select who you'd like to meet";
    if (!form.duration) nextErrors.duration = "Please select a duration";
    if (!form.purpose.trim()) nextErrors.purpose = "Please enter the purpose of the meeting";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      // duration/purpose still aren't confirmed against a real payload, but
      // the selected meeting-with value is sent as `meeting_with` per the
      // confirmed backend field name.
      await createAppointment({
        meeting_with: form.meetingWithId,
        duration: Number(form.duration),
        purpose: form.purpose.trim(),
      });
      enqueueSnackbar("Appointment booked successfully", { variant: "success" });
      navigate("/student-portal/appointments");
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="student-appointment-create">
      <h5 className="sp-section-title mb-1">Book Appointment</h5>
      <p className="student-appointment-subtitle">Schedule a Meeting</p>

      <div className="sp-card">
        <div className="student-appointment-info-box">
          <span className="student-appointment-info-name">{student?.full_name || "Student"}</span>
          <span className="student-appointment-info-id">ID: {student?.id_number || "-"}</span>
        </div>

        {isFeatureUnavailable ? (
          <div className="sp-empty-state">
            <p className="mb-0">Appointments aren&apos;t available for your school yet.</p>
          </div>
        ) : isLoadingTypes ? (
          <Loader />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group mb-3">
              <label htmlFor="appointment-meeting-with" className="mb-1 mt-1 fw-semibold">
                Meeting With <i className="text-danger ms-1">*</i>
              </label>
              <select
                id="appointment-meeting-with"
                className={`form-control ${errors.meetingWithId ? "is-invalid" : ""}`}
                value={form.meetingWithId}
                onChange={handleChange("meetingWithId")}
              >
                <option value="">Select Meeting With</option>
                {types.map((type, index) => {
                  const label = pick(type, ["name", "title", "label"], "Option");
                  const value = type?.id ?? index;
                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  );
                })}
              </select>
              {errors.meetingWithId && <div className="invalid-feedback d-block mt-1">{errors.meetingWithId}</div>}
            </div>

            <div className="form-group mb-3">
              <label htmlFor="appointment-duration" className="mb-1 mt-1 fw-semibold">
                Duration <i className="text-danger ms-1">*</i>
              </label>
              <select
                id="appointment-duration"
                className={`form-control ${errors.duration ? "is-invalid" : ""}`}
                value={form.duration}
                onChange={handleChange("duration")}
              >
                <option value="">Select Duration</option>
                {DURATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.duration && <div className="invalid-feedback d-block mt-1">{errors.duration}</div>}
            </div>

            <div className="form-group mb-3">
              <label htmlFor="appointment-purpose" className="mb-1 mt-1 fw-semibold">
                Purpose <i className="text-danger ms-1">*</i>
              </label>
              <textarea
                id="appointment-purpose"
                className={`form-control ${errors.purpose ? "is-invalid" : ""}`}
                rows={4}
                placeholder="Enter purpose of meeting"
                value={form.purpose}
                onChange={handleChange("purpose")}
              />
              {errors.purpose && <div className="invalid-feedback d-block mt-1">{errors.purpose}</div>}
            </div>

            <Button type="submit" variant="primary" block loading={isSubmitting}>
              Book Appointment
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default StudentAppointmentCreate;
